import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, InfoIcon, Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { Ether, nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { SuggestedGasSettings } from '@root/entities/gas-information'
import {
  GasSettingsPopover,
  GasSettingVariantIcon,
} from '@root/entities/gas-information/ui'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'

import { GAS_VARIANT_LABEL_MAP } from '../constants'

import type { GasSettingsVariant } from '../../../atoms/transfer-fee-reatom'
import type { Error } from '@root/shared'

type GasSettingsRowProps = {
  selected: boolean
  variant: GasSettingsVariant
  settingsState: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    SuggestedGasSettings.SuggestedSettings | undefined
  >
  onClick?: () => void
}

export const GasSettingsRow: React.FC<GasSettingsRowProps> = ({
  variant,
  settingsState,
  selected,
  onClick,
}) => {
  const { t } = useTranslation()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const locale = useDateFnsLocale()
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const renderLabel = () => {
    if (!AsyncStateN.isSuccess(settingsState)) {
      return (
        <SpeedContainer>
          <Skeleton height={28} width={28} />
          <Skeleton height={16} width={50} />
        </SpeedContainer>
      )
    }

    const labelKey = GAS_VARIANT_LABEL_MAP[variant]
    const label = t(labelKey)
    return (
      <SpeedContainer>
        <IconWrapper $selected={selected}>
          <GasSettingVariantIcon variant={variant} size={16} />
        </IconWrapper>
        <SpeedLabel>{label}</SpeedLabel>
      </SpeedContainer>
    )
  }

  const renderWaitTime = () => {
    if (!AsyncStateN.isSuccess(settingsState)) {
      return (
        <TimeContainer>
          <Skeleton height={16} width={60} />
        </TimeContainer>
      )
    }

    const settings = settingsState.value

    if (settings) {
      const formatted = SuggestedGasSettings.formatWaitTime(
        settings.averageWaitTimeEstimate,
        locale,
      )

      if (formatted) {
        return (
          <TimeContainer>
            <TimeIndicator />
            <div>{formatted}</div>
          </TimeContainer>
        )
      }
    }

    return (
      <TimeContainer>
        <div />
      </TimeContainer>
    )
  }

  const renderMaxFee = () => {
    if (!AsyncStateN.isSuccess(settingsState)) {
      return (
        <AmountContainer>
          <Skeleton height={16} width={60} />
        </AmountContainer>
      )
    }

    const settings = settingsState.value

    if (!settings) {
      return
    }

    const formatted = (() => {
      const gwei = SuggestedGasSettings.calculateTransferMaxFeeInGwei(settings)
      const eth = Ether.fromGwei(gwei)
      return <Numeric value={eth} preset="currency-amount-short" />
    })()

    return (
      <AmountContainer>
        <AmountValue>{formatted}</AmountValue>
        <AmountTicker>{nativeCurrency.ticker}</AmountTicker>
      </AmountContainer>
    )
  }

  return (
    <Root $selected={selected} onClick={onClick}>
      {renderLabel()}
      {renderWaitTime()}
      <CommissionContainer>
        {renderMaxFee()}
        <GasSettingsPopover
          open={tooltipOpen}
          onOpenChange={setTooltipOpen}
          variant={variant}
          settings={settingsState.value}
        >
          <Button
            onClick={(e) => {
              e.stopPropagation()
              setTooltipOpen(true)
            }}
            size="xxs"
            variant="soft"
            iconLeft={<InfoIcon />}
          ></Button>
        </GasSettingsPopover>
      </CommissionContainer>
    </Root>
  )
}

const Root = styled.div<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 1fr 0.8fr 1fr;
  justify-items: start;
  align-items: center;

  padding-block: 12px;
  padding-inline: 12px;

  border-bottom: 1px solid var(--backgroundA-2);

  background: ${({ $selected }) =>
    $selected ? 'var(--backgroundA-2)' : undefined};
`

const IconWrapper = styled.div<{ $selected: boolean }>`
  display: flex;
  width: 28px;
  height: 28px;
  justify-content: center;
  align-items: center;

  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected
      ? `radial-gradient(301.86% 237.26% at -13.04% -45.83%, var(--primary-7) 0%, var(--primary-9) 100%)`
      : 'var(--backgroundA-2)'};
`

const SpeedContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 11px;
`

const SpeedLabel = styled.div`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

const TimeContainer = styled.div`
  --color: var(--text-12);

  display: grid;
  grid-template-columns: auto auto;
  gap: 4px;
  align-items: center;

  color: var(--color);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const TimeIndicator = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 2px;

  background: var(--color);
`

const CommissionContainer = styled.div`
  justify-self: end;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 16px;
`

const AmountContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: baseline;
  gap: 4px;
`

const AmountValue = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const AmountTicker = styled.div`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`
